/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Building2 } from 'lucide-react';
import { cn } from '../lib/cn';

interface Props {
  src?: string | null;
  name: string;
  // True when the logo is a light mark on transparency and needs a dark chip
  // background. Never set by hand: detected from the file by
  // src/lib/logoLuminance.js (at request time on the server and in the bake,
  // as `logoOnDark` on the payload; LogoPreview's twin in the admin and the
  // portal), and an opaque logo always answers false. Defaults to false (white
  // chip — fits coloured and dark logos).
  onDark?: boolean;
  className?: string;
  imgClassName?: string;
  fallback?: 'icon' | 'initial';
  iconClassName?: string;
  alt?: string;
  loading?: 'eager' | 'lazy';
  width?: number;
  height?: number;
  // Wordmark logos (aspect ≥ 2.2) may widen the chip up to max-w-32 so the mark
  // stays legible. Pass false where chips sit in a uniform list and must all
  // measure the same (the /providers overview): a widened chip there reads
  // as a bigger logo, not a more legible one.
  allowWide?: boolean;
}

// Up to two letters drawn from the first two non-trivial words of the brand
// name. Strips legal suffixes and common stopwords so "Achilles Brandstoffen
// Maatschappij B.V." → "AB", "Slimere" → "SL", "MKB Stadslogistiek" → "MS".
const STOPWORDS = new Set([
  'b.v.',
  'bv',
  'b.v',
  'n.v.',
  'nv',
  'v.o.f.',
  'vof',
  'holding',
  'group',
  'groep',
  'the',
  'de',
  'het',
  'van',
  'der',
  'den',
  'and',
  'en',
  '&',
]);

function deriveInitials(name: string): string {
  const cleaned = name.replace(/[()]/g, ' ').trim();
  if (!cleaned) return '?';
  const words = cleaned.split(/[\s\-_./]+/).filter((w) => w && !STOPWORDS.has(w.toLowerCase()));
  if (words.length === 0) return cleaned.charAt(0).toUpperCase();
  if (words.length === 1) {
    const w = words[0];
    const letters = w.replace(/[^A-Za-z0-9]/g, '');
    return letters.slice(0, 2).toUpperCase() || '?';
  }
  const first = words[0].replace(/[^A-Za-z0-9]/g, '').charAt(0);
  const second = words[1].replace(/[^A-Za-z0-9]/g, '').charAt(0);
  return (first + second).toUpperCase() || '?';
}

// A wordmark: a logo whose natural aspect ratio is at least this wide. Read
// straight off the decoded image — the chip has no other input. A broken image
// reports 0×0 and so never counts.
const WORDMARK_ASPECT = 2.2;

function isWordmark(img: HTMLImageElement): boolean {
  return img.naturalHeight > 0 && img.naturalWidth / img.naturalHeight >= WORDMARK_ASPECT;
}

// One kind of image is complete at 0×0 while rendering perfectly: an SVG with no
// intrinsic size (no width, height or viewBox), which Firefox reports as 0×0.
// Logo URLs are third-party, so such a file is possible, and treating it as
// broken would replace a working logo with initials. Skipping SVG sources can
// only leave the status quo — a broken SVG keeps the browser's glyph, as every
// broken logo did until now — so the blind spot costs less than the false
// positive. Extension-less URLs that serve SVG are out of reach either way.
function isSvgSource(src: string): boolean {
  const value = src.trim().toLowerCase();
  if (value.startsWith('data:image/svg+xml')) return true;
  return value.split('#')[0].split('?')[0].endsWith('.svg');
}

// `complete` is true for two opposite outcomes — "completely available" and
// "broken" — so the natural size is what separates them: a broken image reports
// 0×0 on both axes. The `complete` check stays inside the test because a still
// pending image measures 0×0 as well, and calling that one broken would blank
// every logo that has not arrived yet.
function isBroken(img: HTMLImageElement, src: string): boolean {
  return img.complete && img.naturalWidth === 0 && img.naturalHeight === 0 && !isSvgSource(src);
}

export const ProviderLogo: React.FC<Props> = ({
  src,
  name,
  onDark = false,
  className,
  imgClassName,
  fallback = 'icon',
  iconClassName,
  alt,
  loading = 'lazy',
  width,
  height,
  allowWide = true,
}) => {
  // A 404'ing or blocked logo URL should degrade to the initials/icon
  // fallback, not the browser's broken-image glyph (logos are third-party
  // hosted, so individual URLs do rot).
  const [failed, setFailed] = React.useState(false);
  // True once the loaded image turns out to be a wordmark (very wide aspect).
  // Such a mark is illegible squeezed into a square chip, so by default the
  // chip widens and the mark keeps its full height. With `allowWide={false}`
  // the chip keeps its given size and the mark simply renders smaller.
  const [isWide, setIsWide] = React.useState(false);
  const widen = allowWide && isWide;
  const imgRef = React.useRef<HTMLImageElement>(null);

  // Both flags describe the current src — reset when it changes, then measure
  // whatever the element already holds. React attaches `onLoad` and `onError` at
  // commit, so an image that settled before that — the SSR-baked chip on a
  // hydrated page, a logo served straight from cache — fires neither. The chip
  // only widened on visits where the logo happened to arrive late (2 of 25
  // wordmarks on /providers at 1280 px vs 18 of 25 at 375 px, measured
  // 2026-09-04), and a URL that had already 404'd kept the browser's
  // broken-image glyph instead of the initials — on a prerendered page every
  // logo resolves before hydration, so that was the common case, not the rare
  // one. Reading the settled state here makes both outcomes independent of load
  // timing; the handlers still cover images that settle later, including a src
  // swapped on a mounted element (that always re-fires them). The effect runs
  // only on the client, after hydration, so the first render stays identical to
  // the server's.
  React.useEffect(() => {
    setFailed(false);
    setIsWide(false);
    const img = imgRef.current;
    // While a new src is still pending, `complete` is false and the natural size
    // still describes the previous image — hence the guard in both tests below.
    if (!src || !img) return;
    if (isBroken(img, src)) setFailed(true);
    else if (img.complete && isWordmark(img)) setIsWide(true);
  }, [src]);

  // Literal hexes (not slate/white tokens): a logo's chip must match the logo,
  // not the page theme. The slate/white tokens remap in light mode (white →
  // dark, slate-900 → near-white), which would invert both chips and hide the
  // logo. White/light logos always need a dark chip; coloured/dark logos always
  // need a white chip — in both themes.
  const surfaceClass = onDark ? 'bg-[#0f172a] border-[#334155]' : 'bg-[#ffffff] border-slate-600';

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden shrink-0 border shadow-sm',
        surfaceClass,
        className,
        // Important on purpose: consumers size the chip per breakpoint
        // (`w-10 h-10 md:w-12 md:h-12`) and tailwind-merge only drops the
        // unprefixed width for `w-auto`, so from md up the fixed width stayed
        // and the wide mark was cropped to its middle 48 px. `!` wins at every
        // breakpoint; max-w-32 still caps the growth.
        widen && 'w-auto! max-w-32 px-2'
      )}
    >
      {src && !failed ? (
        <img
          ref={imgRef}
          src={src}
          alt={alt ?? name}
          width={width}
          height={height}
          loading={loading}
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          onLoad={(e) => {
            if (isWordmark(e.currentTarget)) setIsWide(true);
          }}
          className={cn('h-full object-contain', widen ? 'w-auto' : 'w-full', imgClassName)}
        />
      ) : fallback === 'initial' ? (
        <div
          aria-label={name}
          className={cn(
            'w-full h-full flex items-center justify-center font-black tracking-tight',
            onDark ? 'bg-[#0f172a] text-[#f8fafc]' : 'bg-[#ffffff] text-[#0f172a]'
          )}
        >
          {deriveInitials(name)}
        </div>
      ) : (
        <Building2
          className={cn(onDark ? 'text-[#94a3b8]' : 'text-slate-500', iconClassName ?? 'w-5 h-5')}
        />
      )}
    </div>
  );
};
